import { toExcelDate } from 'components/xlsx/excelCells';
import { ClassifierData } from 'types/classifiers';
import { ContactListExportItem } from 'types/contacts';
import { ExportMapperOptions } from 'types/exports';
import { getClassifierText, t } from 'utils/getTranslations';
import { EFFORT_ACTIVITY_TYPE, lastEffortTypeOf } from 'utils/lastEffortType';

export const mapContactsToCSVFormat = (
    contacts: ContactListExportItem[],
    classifiers: Record<string, ClassifierData>,
    options?: ExportMapperOptions
) => {
    const lastEffortType = options?.isLastHcsMeetingEnabled
        ? lastEffortTypeOf(classifiers[EFFORT_ACTIVITY_TYPE])
        : null;
    const lastBdEffortHeader = t('contacts.export.last-effort');
    // A type named after the BD-effort column would overwrite that column's key in the row: its code tells them apart.
    const lastEffortTypeHeader =
        lastEffortType &&
        (lastEffortType.label === lastBdEffortHeader
            ? `${lastEffortType.label} (${lastEffortType.code})`
            : lastEffortType.label);
    return contacts.map((contact) => ({
        [t('contacts.export.client-number')]: contact.clientNumber ?? '',
        [t('contacts.export.contact-name')]: contact.name,
        [t('contacts.export.reg-nr-or-id-code')]: contact.regNrOrIdCode ?? '',
        [t('contacts.export.position')]: contact.position ?? '',
        [t('contacts.export.group-name')]: (contact.partyGroups ?? []).map((group) => group.groupName).join(', '),
        [t('contacts.export.primary-contact-name')]: contact.primaryContact?.name ?? '',
        [t('contacts.export.primary-contact-email')]: contact.primaryContact?.mainEmail ?? '',
        [t('contacts.export.primary-contact-position')]: contact.primaryContact?.position ?? '',
        [t('contacts.export.primary-contact-language')]: getClassifierText(
            classifiers['LANGUAGE'],
            contact.primaryContact?.preferredLanguageCode ?? ''
        ),
        [t('contacts.export.main-email')]: contact.mainEmail ?? '',
        [t('contacts.export.mobile-phone')]: contact.mainMobile ?? '',
        [t('contacts.export.employed-by')]: (contact.employedByCompanies ?? [])
            .map((employer) => employer.officialName)
            .join(', '),
        [t('contacts.export.employer-segment')]: (contact.employedByCompanies ?? [])
            .flatMap((employer) => {
                if (!employer.segmentTypeCode) return [];
                const text = getClassifierText(classifiers['SEGMENT_TYPE'], employer.segmentTypeCode);
                return text ? [text] : [];
            })
            .join(', '),
        [t('contacts.export.employer-service-level')]: (contact.employedByCompanies ?? [])
            .flatMap((employer) => {
                if (!employer.serviceLevelTypeCode) return [];
                const text = getClassifierText(classifiers['SERVICE_LEVEL_TYPE'], employer.serviceLevelTypeCode);
                return text ? [text] : [];
            })
            .join(', '),
        [t('contacts.export.business-area')]: (contact.businessAreas ?? [])
            .map(({ businessAreaCode }) => getClassifierText(classifiers['BUSINESS_AREA_TYPE'], businessAreaCode))
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .join(', '),
        [t('contacts.export.sector-group')]: (contact.servedBySectorGroups ?? [])
            .map((sg) => sg.abbreviation)
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .join(', '),
        [t('contacts.export.segmentation-level')]: getClassifierText(
            classifiers['SEGMENT_TYPE'],
            contact.segmentTypeCode
        ),
        [t('contacts.export.service-level')]: getClassifierText(
            classifiers['SERVICE_LEVEL_TYPE'],
            contact.serviceLevelTypeCode
        ),
        [t('contacts.export.managers')]: (contact.managers ?? []).map(({ employee }) => employee.name).join(', '),
        [t('contacts.export.partners')]: (contact.partners ?? []).map(({ employee }) => employee.name).join(', '),
        [lastBdEffortHeader]: toExcelDate(contact.lastBdEffort),
        ...(lastEffortTypeHeader ? { [lastEffortTypeHeader]: toExcelDate(contact.lastHCSBdEffort) } : {}),
        [t('contacts.export.offices')]: (contact.servedByOffices ?? [])
            .map((office) => office.abbreviation)
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .join(', '),
        [t('contacts.export.practice-groups')]: (contact.servedByPracticeGroups ?? [])
            .map((pg) => pg.abbreviation)
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .join(', '),
    }));
};
